import CryptoJS from 'crypto-js';
import { BillingTypeEunm, BusinessTypeMap, BusinessTypeEnum, SkuEnum, ERROR_CODE_MESSAGES } from '@/utils/constants';
import dayjs from 'dayjs';
/** 固定 Header 高度，滚动锚点需减去该值 */
const HEADER_HEIGHT = 100;

/**
 * 将后端返回的业务结果中的 errorMsg 按错误码映射表替换为友好中文提示。
 * 若 errorCode 命中映射表，则原地改写 errorMsg 后返回；否则原样返回。
 */
export function localizeBusinessError(result) {
    if (!result || typeof result !== 'object' || result.errorCode == null) return result;
    const friendly = ERROR_CODE_MESSAGES[result.errorCode];
    if (friendly != null) result.errorMsg = friendly;
    return result;
}

export const ENV = import.meta.env.VITE_ENV || "dev";
/**
 * 单个 web 资源路径 → 完整 URL
 */
export function resolveWebAsset(path) {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) {
        return path;
    }
    
    // ✨ 关键改动：把变量的获取移入函数内部！
    // 只有在函数真正被调用时才会去读取环境变量，从而彻底避开文件加载时的初始化顺序问题。
    const baseUrl = import.meta?.env?.VITE_WEB_ASSETS_URL || '';
    
    const base = baseUrl.replace(/\/$/, '');
    const file = String(path).replace(/^\//, '');
    if (!base) {
        return `/${file}`;
    }
    return `${base}/${file}`;
}

/**
 * 批量处理 webAssets 对象，给每个 value 拼上 CDN 前缀
 */
export function resolveWebAssets(assets) {
    return Object.fromEntries(
        Object.entries(assets).map(([key, value]) => [key, resolveWebAsset(value)])
    );
}


/**
 * 密码加密函数
 */
export const encryptPassword = (password) => {
    return CryptoJS.enc.Base64.stringify(CryptoJS.SHA256(password));
};

/**
 * 签名计算函数
 */
export const calSign = (path, obj = null, accessToken = null) => {
    let finalStr = "moldai" + ":" + path;
    const timestampStr = Date.now();
    finalStr += ":" + timestampStr;

    if (obj) {
        const sortedObj = Object.keys(obj).sort().reduce((acc, key) => {
            acc[key] = obj[key];
            return acc;
        }, {});
        const dataStr = JSON.stringify(sortedObj);
        finalStr += ":" + dataStr;
    }

    if (accessToken) {
        finalStr += ":" + accessToken;
    }

    const hash = CryptoJS.MD5(finalStr);
    const hashInBase64 = CryptoJS.enc.Base64.stringify(hash);
    let authorizationStr = "timestamp:" + timestampStr + "," + "sign:" + hashInBase64;

    if (accessToken) {
        authorizationStr += ",accessToken:" + accessToken;
    }

    return authorizationStr;
};



/** 将 DOM 元素滚到视口内（减去固定 Header 高度） */
export function scrollElementIntoView(element, offset = HEADER_HEIGHT) {
    if (!element) return;
    const top = element.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
}

export function scrollToTop({ behavior = 'smooth' } = {}) {
    window.scrollTo({ top: 0, left: 0, behavior });
}


export const formatNumber = (num) => {
    if (num == null) return '0';
    return num.toLocaleString();
};

// 环境判断工具函数
export const isDevEnv = () => ENV === "dev";
export const isTestEnv = () => ENV === "test"; 
export const isProdEnv = () => ENV === "prod";


// 环境配置 - 根据Vite环境变量动态获取
export const getApiBaseUrl = () => {
    // 开发环境使用环境变量，生产环境使用构建时注入的变量
    if (import.meta.env.DEV) {
      return import.meta.env.VITE_API_BASE_URL || "https://testapi.moldai.net";
    }
    return import.meta.env.VITE_API_BASE_URL || "https://testapi.moldai.net";
};





/** ISO 时间 → "YYYY-MM-DD HH:mm" */
export const formatTime = (isoStr) => {
    if (!isoStr) return '';
    const d = new Date(isoStr);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};


/**
 * 生成重建任务的分享链接
 * 环境变量 VITE_WEB_PREVIEW_URL 只存路径片段（不含域名）：
 *   开发/测试：/webPreviewScene.html#/WebRebuildPage
 *   生产：/demo/webPreviewScene.html#/WebRebuildPage
 * 域名动态取当前页面的窗口 origin，再拼接环境变量中的路径。
 * @param {string} taskId 任务 id
 * @returns {string} 完整的分享链接
 */
const WEB_PREVIEW_PATH = import.meta.env.VITE_WEB_PREVIEW_URL || '';
export const generateShareUrl = (taskId) => {
    return `${window.location.origin}${WEB_PREVIEW_PATH}?taskId=${taskId}`;
};

/** 文件大小换算单位 */
const FILE_SIZE_MB = 1024 * 1024;
const FILE_SIZE_GB = 1024 * FILE_SIZE_MB;

/**
 * 将字节数格式化为易读大小（如 18MB / 2.1GB）
 * @param {number} bytes 字节数
 * @returns {string} 格式化后的大小
 */
export const formatFileSize = (bytes) => {
    if (!bytes) return '0MB';
    if (bytes >= FILE_SIZE_GB) return `${(bytes / FILE_SIZE_GB).toFixed(1)}GB`;
    return `${Math.round(bytes / FILE_SIZE_MB)}MB`;
};

/** 单位换算基数：1GB = 1024MB */
const GB_IN_MB = 1024;

/**
 * 智能格式化 MB 值为可读体积：达到 1GB 时切换为 GB，否则仍以 MB 显示
 * @param {number|string} sizeMB - 体积数值（MB 为单位）
 * @returns {string} 如 "512 MB" / "1.2 GB"
 */
export const formatSizeUnit = (sizeMB) => {
    if (sizeMB == null || sizeMB === '') return '';
    const mb = Number(sizeMB);
    if (Number.isNaN(mb)) return `${sizeMB} MB`;
    if (mb >= GB_IN_MB) {
        return `${parseFloat((mb / GB_IN_MB).toFixed(2))} GB`;
    }
    return `${mb} MB`;
};



/**
 * 根据流水类型和数据生成详细的计费规则描述
 * @param {Object} item 接口返回的单条流水记录
 * @returns {string} 构造好的详情字符串
 */
export const generateBillingDetails = (item) => {
    const type = Number(item.transaction_type);
    const info = item.info || {};
    const billingRule = info.billing_rule || {};
    const taskParam = info.task_param || {};

    // 需要显示计费公式的业务类型列表
    const formulaBusinessTypes = [
        BusinessTypeEnum.SC,
        BusinessTypeEnum.SC_EXPORT,
        BusinessTypeEnum.FORMAT_CONVERT,
        BusinessTypeEnum.DSM_DOM,
        BusinessTypeEnum.DENSE_POINTCLOUD,
        BusinessTypeEnum.MESH,
        BusinessTypeEnum.GS_SELF,
        BusinessTypeEnum.GS_GAODE,
        BusinessTypeEnum.GS_THIRD
    ];

    // 1. 计费公式类流水：扣费、冻结、返还
    if (
        type === BillingTypeEunm.DEDUCT || 
        type === BillingTypeEunm.FREEZE || 
        type === BillingTypeEunm.RELEASE
    ) {
        if (formulaBusinessTypes.includes(info.business_type)) {
            // 从 info 中安全提取字段，并提供默认值
            const basePoints = billingRule.base_points ?? 0;
            const weightFactor = billingRule.weight_factor ?? 1;
            const imageFactor = billingRule.image_factor ?? 1;
            const imageCount = taskParam.imageCount ?? 0;
            
            // 提取业务类型的中文描述
            const businessName = BusinessTypeMap[item.business_type] || '业务';
            
            // 判断是否存在成功 Tile 数
            const hasSuccessTiles = taskParam.successTiles !== undefined && taskParam.successTiles !== null;
            const successTiles = taskParam.successTiles ?? 1;

            // 按公式实际计算积分值
            let pointsResult = basePoints * weightFactor * imageFactor;
            if (hasSuccessTiles) {
                pointsResult *= successTiles;
            }

            // 动态拼接公式字符串
            let formulaStr = `基础底价${basePoints} × ${businessName}权重${weightFactor} × 照片因子${imageFactor}(${imageCount}张)`;
            if (hasSuccessTiles) {
                formulaStr += ` × 成功Tile${successTiles}`;
            }
            formulaStr += ` = ${pointsResult}积分`;

            return formulaStr;
        }else{
            let action = '';
            if (type === BillingTypeEunm.DEDUCT) action = '扣除';
            if (type === BillingTypeEunm.FREEZE) action = '冻结';
            if (type === BillingTypeEunm.RELEASE) action = '返还';
            return  `生成式积分${action}`;
        }
    }

    // 2. 非计费公式类流水：充值、过期、赠送、退款
    const nonBillingMap = {
        [BillingTypeEunm.RECHARGE]: '',
        [BillingTypeEunm.EXPIRE]: '(过期)',
        [BillingTypeEunm.GIFT]: '(套餐赠送)',
        [BillingTypeEunm.REFUND]: '(系统异常扣除退款)',
    };

    if (nonBillingMap[type] !== undefined) {
        const suffix = nonBillingMap[type];
        return `非计费公式类流水\n固定积分变动${suffix}`;
    }

    // 兜底返回
    return item.info ? JSON.stringify(item.info) : '-';
};



/**
 * 防抖函数
 * @param {Function} fn - 目标执行函数
 * @param {number} delay - 防抖延迟时间(ms)
 * @param {boolean} immediate - 是否立即执行：true=首次触发立即执行，false=最后一次触发后延迟执行
 * @returns {Function} 防抖包装后的函数
 */
export const debounce = (fn, delay, immediate = false) => {
    let timer = null;
  
    return function (...args) {
      // 清空上一次定时器
      if (timer) clearTimeout(timer);
  
      // 立即执行模式：仅首次触发执行，后续等待冷却
      if (immediate && !timer) {
        fn.apply(this, args);
      }
  
      timer = setTimeout(() => {
        // 非立即执行：延迟后执行函数
        if (!immediate) {
          fn.apply(this, args);
        }
        timer = null;
      }, delay);
    };
};


/**
 * 节流函数（首次立即执行，冷却期内的调用被丢弃）
 * 适合限制 message 等提示的刷屏：一批文件连续触发时只提示第一条
 * @param {Function} fn - 目标执行函数
 * @param {number} interval - 节流间隔(ms)，此期间的调用直接忽略
 * @returns {Function} 节流包装后的函数
 */
export const throttle = (fn, interval) => {
    let lastTime = 0;

    return function (...args) {
        const now = Date.now();
        if (now - lastTime < interval) return;
        lastTime = now;
        fn.apply(this, args);
    };
};


// 获取今天的时间  23：59
export const getDaysAgo = (days = 30) => {
    return dayjs().subtract(days, 'day').startOf('day').toISOString();
};

// 获取今天结束时间 23:59:59
export const getTodayEnd = () => {
    return dayjs().endOf('day').toISOString();
};

// sku 编号 → 中文标签映射
const SkuLabelMap = {
    [SkuEnum.YEAR]: '年付',
    [SkuEnum.MONTH]: '月付',
    [SkuEnum.DAY]: '日付',
};

/**
 * 根据产品名和 sku 拼接购买内容展示字符串
 * @param {string} productName - 产品名称
 * @param {number|null} sku - SKU 枚举值
 * @returns {string} 如 "专业版 年付订阅" 或 "专业版"
 */
export const formatProductName = (productName, sku) => {
    if (sku != null && Object.values(SkuEnum).includes(sku)) {
        return `${productName} ${SkuLabelMap[sku]}订阅`;
    }
    return productName;
};

/**
 * 密码校验规则：长度 8-14 位，全为 ASCII 可见字符（字母、数字、英文标点），
 * 且字母/数字/标点三类中至少包含两类；不支持空格、中文、emoji 及其他符号。
 * @param {string} pw - 待校验的密码
 * @returns {{ ok: boolean, msg: string }} 校验结果与对应的提示语（ok=false 时 msg 即失败原因）
 */
export const validatePassword = (pw) => {
    if (typeof pw !== "string" || pw.length === 0) {
        return { ok: false, msg: "请输入密码" };
    }
    if (pw.length < 8) {
        return { ok: false, msg: `密码至少 8 位（还差 ${8 - pw.length} 位）` };
    }
    if (pw.length > 14) {
        return { ok: false, msg: "密码不能超过 14 位" };
    }
    if (/\s/.test(pw)) {
        return { ok: false, msg: "密码不能包含空格" };
    }

    // 字符类别判断
    const isLetter = (c) => /[A-Za-z]/.test(c);
    const isDigit = (c) => /[0-9]/.test(c);
    // 英文标点：ASCII 可见字符中，既不是字母也不是数字的部分
    const isPunct = (c) => /^[\x21-\x7E]$/.test(c) && !isLetter(c) && !isDigit(c);

    let hasLetter = false, hasDigit = false, hasPunct = false;
    for (const ch of pw) {
        if (isLetter(ch)) hasLetter = true;
        else if (isDigit(ch)) hasDigit = true;
        else if (isPunct(ch)) hasPunct = true;
        else {
            return { ok: false, msg: "包含不支持的字符（如中文、emoji 或控制符）" };
        }
    }

    const kinds = [hasLetter, hasDigit, hasPunct].filter(Boolean).length;
    if (kinds < 2) {
        return { ok: false, msg: "字母、数字、标点中至少包含两类" };
    }

    return { ok: true, msg: "✓ 密码格式正确" };
};

/**
 * 生成文件唯一 ID
 */
export const generateFileId = (file) => {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}-${file.name}`;
};

/** 取文件扩展名（小写），不做归一化：jpg 与 jpeg 视为不同类型 */
const getImageExt = (name) => String(name).split('.').pop().toLowerCase();

/**
 * 上传前置校验：图片格式必须统一（jpg / jpeg / png 互相独立）
 * 规则：本批第一张的类型代表整批，imageFileList 第一项的类型代表整个列表
 * 1. 批内一致性：本批所有文件与第一张类型一致，混用拒绝整批
 * 2. 与已有列表一致：本批第一张与列表第一项类型一致，不一致拒绝整批
 * @param {File[]} files - 本次选择的文件列表（非空）
 * @param {Array<{name: string}>} existingList - 当前已存图片列表
 * @returns {{ ok: boolean, msg: string }} ok=false 时 msg 为提示语
 */
export const validateUniformImageType = (files, existingList = []) => {
    const batchExt = getImageExt(files[0].name);

    // 1. 批内一致性：与第一张不同即为混用
    if (files.some((f) => getImageExt(f.name) !== batchExt)) {
        return { ok: false, msg: '同一批上传的图片格式必须统一，请确保全部为同一格式（JPG / JPEG / PNG 互不混用）' };
    }

    // 2. 与已有列表第一项比较（列表为空则无需比较）
    if (existingList.length > 0 && getImageExt(existingList[0].name) !== batchExt) {
        return { ok: false, msg: `当前列表为 ${getImageExt(existingList[0].name).toUpperCase()} 格式，请保持文件格式统一` };
    }

    return { ok: true, msg: '' };
};

/**
 * 用 PUT 方式将 file 上传到指定 url（预签名直传 / 本地 demo 服务器）
 * 基于 fetch 实现，不支持上传进度回调
 * @param {string} url - 上传目标地址（预签名 upload_url）
 * @param {File|Blob} file - 待上传文件（原始文件或压缩后的 Blob）
 * @returns {Promise<void>} 上传成功 resolve，失败 reject(new Error(msg))
 */
export const putUpload = async (url, file) => {
    // 刻意不设置 Content-Type：后端预签名 URL 签名时 Content-Type 为空值，
    // 若请求带上该头（如 image/jpeg），OSS 重算签名与 URL 中的 Signature 不一致，返回 403。
    // 注意：body 直接传 File 时，fetch 会自动把 File.type 作为 Content-Type 发出，
    // 因此包一层无类型的 Blob，让请求真正不携带 Content-Type。
    const res = await fetch(url, {
        method: 'PUT',
        body: new Blob([file]),
    });

    if (!res.ok) {
        throw new Error(`上传失败（${res.status}）`);
    }
};
/**
 * 简单并发调度器：控制同时执行的最大任务数，超出部分进入队列
 * @example
 *   const scheduler = new Scheduler(5);
 *   await Promise.all(tasks.map((task) => scheduler.add(() => upload(task))));
 */
/**
 * 等待浏览器渲染完下一帧（双 rAF），用于 setState 后先让 UI 刷新再执行耗时逻辑
 * @returns {Promise<void>}
 */
export const nextFrame = () => new Promise((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(resolve))
);

export class Scheduler {
    /**
     * @param {number} max - 最大并发数
     */
    constructor(max) {
        this.max = max;       // 最大并发数
        this.count = 0;       // 当前正在执行的任务数
        this.queue = [];      // 等待执行的任务队列（存的是 resolve 函数）
    }

    /**
     * 提交一个返回 Promise 的任务，并发超限时自动排队
     * @param {() => Promise} promiseCreator - 返回 Promise 的任务函数
     * @returns {Promise} 任务完成后 resolve
     */
    async add(promiseCreator) {
        // 如果当前并发已满，把任务的 resolve 塞进队列，并 await 一个阻塞的 Promise
        if (this.count >= this.max) {
            await new Promise((resolve) => this.queue.push(resolve));
        }

        // 有空位了，开始执行任务
        this.count++;
        try {
            return await promiseCreator();
        } finally {
            this.count--;
            // 执行完一个，如果队列里还有排队的任务，放一个进去
            if (this.queue.length) {
                this.queue.shift()(); // 解除那个任务的阻塞
            }
        }
    }
}

/** 图片压缩：输出图片的最长边(px) */
const IMAGE_MAX_EDGE = 800;
/** 图片压缩：JPEG 输出质量 */
const IMAGE_QUALITY = 0.8;

/**
 * 对图片 File 解码并压缩，生成 Blob
 * @param {File} file - 原始图片文件
 * @param {number} maxEdge - 输出图片最长边限制(px)，原图更小时不会放大
 * @param {number} quality - JPEG 输出质量(0~1)
 * @returns {Promise<Blob>} 压缩后的 Blob（image/jpeg）
 */
export const compressImageToBlob = async (file, maxEdge = IMAGE_MAX_EDGE, quality = IMAGE_QUALITY) => {
    const bitmap = await createImageBitmap(file);
    try {
        // 只缩不放：原图比 maxEdge 小时保持原尺寸
        const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
        const width = Math.max(1, Math.round(bitmap.width * scale));
        const height = Math.max(1, Math.round(bitmap.height * scale));

        const canvas = new OffscreenCanvas(width, height);
        canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height);

        return await canvas.convertToBlob({ type: 'image/jpeg', quality });
    } finally {
        // 无论成功失败都要释放解码出来的位图，否则内存不会被及时回收
        bitmap.close();
    }
};

/** 网页端任务数量上限 */
export const MAX_TASK_COUNT = 3;

/**
 * 生成「任务数量超限」提示弹框内容（复用 UploadLimitModal）
 * @returns {{ title: string, subtitle: string, lines: Array }}
 */
export const buildTaskLimitData = () => ({
    title: '任务数量超出限制',
    subtitle: `网页端最多支持 ${MAX_TASK_COUNT} 个任务`,
    lines: [
        { text: `您已完成 ${MAX_TASK_COUNT} 个任务。创建更多项目，获取更高精度，请下载 moldia 桌面端。` },
    ],
});

/** 封面缩略图的 OSS 图片处理参数：等比缩放到宽 600（lfit，不裁剪不变形）+ q_80 质量压缩 */
const OSS_COVER_PROCESS = 'x-oss-process=image/resize,lfit,w_600/quality,q_80';

/**
 * 为任务列表的 cover_url 批量追加 OSS 图片处理参数（服务端等比缩放+压缩，加速封面加载）
 * @param {Array<{cover_url?: string}>} tasks - getTask 返回的 res.data.tasks
 * @returns {Array} 新数组；cover_url 为空或已带 x-oss-process 的条目原样返回
 */
export const processTaskCoverUrls = (tasks = []) =>
    tasks.map((task) => {
        const url = task?.cover_url;
        if (!url || url.includes('x-oss-process')) return task;
        const sep = url.includes('?') ? '&' : '?';
        return { ...task, cover_url: `${url}${sep}${OSS_COVER_PROCESS}` };
    });


